import React, { useContext, useState, useCallback, useRef } from 'react';
import { CompetitionContext } from '../contexts/CompetitionContext';
import { Container, Alert, Card } from 'react-bootstrap';
import { registerActivity } from '../services/ActivityService';
import Loading from '../components/Common/Loading';
import RegisterActivityForm from '../components/RegisterActivityForm/RegisterActivityForm';

const RegisterActivityPage = () => {
  const [activityData, setActivityData] = useState({
    type: '',
    distance: 0,
    durationHours: 0,
    durationMinutes: 0,
    durationSeconds: 0,
    elevation: 0,
    calories: 0,
    date: ''
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [submitMessage, setSubmitMessage] = useState('');
  const { isLoading, setIsLoading } = useContext(CompetitionContext);
  const [attachments, setAttachments] = useState([]);

  const typeSelectRef = useRef(null);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setActivityData(prevData => ({
      ...prevData,
      [name]: ['distance', 'elevation', 'calories', 'durationHours', 'durationMinutes', 'durationSeconds'].includes(name) ? parseFloat(value) : value
    }));
  }, []);

  const handleFileChange = useCallback((e) => {
    const files = Array.from(e.target.files);
    setAttachments(files.map(file => ({ file, name: file.name, type: file.type })));
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setIsLoading(true);
    try {
      console.log('Submitting activity data:', activityData);
      const totalDurationInSeconds =
        (activityData.durationHours * 3600) +
        (activityData.durationMinutes * 60) +
        activityData.durationSeconds;

      const { durationHours, durationMinutes, durationSeconds, ...activityDataWithoutDuration } = activityData;

      const response = await registerActivity({
        ...activityDataWithoutDuration,
        duration: totalDurationInSeconds,
        attachments
      });

      const newActivity = response.data.data;
      setSubmitMessage('Well done! You accumulated ' + String(newActivity.points) + " points");
      setActivityData({
        type: '',
        distance: 0,
        durationHours: 0,
        durationMinutes: 0,
        durationSeconds: 0,
        calories: 0,
        elevation: 0,
        date: '',
        attachments: []
      });

      // Reset the file input by clearing its value
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) {
        fileInput.value = '';
      }

      typeSelectRef.current.focus();
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      const errorMessage = error.response?.data?.message || 'Failed to register activity';
      setErrorMessage(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [activityData, attachments, setIsLoading]);

  return (
    <Container>
      <Card>
        <Card.Body>
          <h1>Register New Activity</h1>
          <RegisterActivityForm
            activityData={activityData}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            isLoading={isLoading}
            typeSelectRef={typeSelectRef}
            handleFileChange={handleFileChange} // Pass the file change handler
          />
        </Card.Body>
      </Card>
      {submitMessage &&
        <Card style={{ background: 'transparent', border: 'none' }}>
          <Card.Body>
            <Alert variant="success" onClose={() => setSubmitMessage('')} dismissible>
              <Alert.Heading>Activity registered successfully!</Alert.Heading>
              <p>{submitMessage}</p>
            </Alert>
          </Card.Body>
        </Card>
      }
      {errorMessage &&
        <Card style={{ background: 'transparent', border: 'none' }}>
          <Card.Body>
            <Alert variant="danger" onClose={() => setErrorMessage('')} dismissible>
              <Alert.Heading>Oh snap! You got an error!</Alert.Heading>
              <p>{errorMessage}</p>
            </Alert>
          </Card.Body>
        </Card>
      }
      {isLoading && (
        <Card style={{ background: 'transparent', border: 'none' }}>
          <Card.Body>
            <Card.Text className='text-center'>Hold on, while we save your data!</Card.Text>
            <Loading />
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default RegisterActivityPage;
